import React from 'react';
import {
  Box,
  Skeleton,
  CircularProgress,
  Typography,
  LinearProgress,
  alpha,
} from '@mui/material';

// Full page loading spinner
export const PageLoader = ({ message = 'Cargando...' }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 400,
      gap: 2,
    }}
  >
    <CircularProgress size={48} thickness={4} />
    <Typography variant="body2" color="text.secondary">
      {message}
    </Typography>
  </Box>
);

// Table skeleton loader
export const TableSkeleton = ({ rows = 5, columns = 5 }) => (
  <Box>
    {/* Header */}
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        p: 2,
        backgroundColor: '#f8fafc',
        borderBottom: '2px solid #e2e8f0',
      }}
    >
      {Array.from({ length: columns }).map((_, i) => (
        <Skeleton key={i} variant="text" width={`${100 / columns}%`} height={24} />
      ))}
    </Box>
    {/* Rows */}
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <Box
        key={rowIndex}
        sx={{
          display: 'flex',
          gap: 2,
          p: 2,
          borderBottom: '1px solid #f1f5f9',
        }}
      >
        {Array.from({ length: columns }).map((_, colIndex) => (
          <Skeleton
            key={colIndex}
            variant="text"
            width={`${100 / columns}%`}
            height={20}
            animation="wave"
          />
        ))}
      </Box>
    ))}
  </Box>
);

// Card skeleton loader
export const CardSkeleton = ({ count = 4 }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 3 }}>
    {Array.from({ length: count }).map((_, index) => (
      <Box
        key={index}
        sx={{
          p: 3,
          borderRadius: 2,
          border: '1px solid #e2e8f0',
          backgroundColor: 'white',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Skeleton variant="circular" width={48} height={48} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="80%" height={24} />
            <Skeleton variant="text" width="60%" height={36} />
            <Skeleton variant="text" width="40%" height={16} />
          </Box>
        </Box>
      </Box>
    ))}
  </Box>
);

// Stats skeleton loader
export const StatsSkeleton = ({ count = 4 }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 3 }}>
    {Array.from({ length: count }).map((_, index) => (
      <Box
        key={index}
        sx={{
          p: 3,
          borderRadius: 2,
          border: '1px solid #e2e8f0',
          backgroundColor: 'white',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width={80} height={16} />
            <Skeleton variant="text" width={100} height={48} sx={{ mt: 1 }} />
            <Skeleton variant="text" width={120} height={16} sx={{ mt: 1 }} />
          </Box>
          <Skeleton variant="rounded" width={56} height={56} sx={{ borderRadius: 3 }} />
        </Box>
      </Box>
    ))}
  </Box>
);

// Form skeleton loader
export const FormSkeleton = ({ fields = 4 }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
    {Array.from({ length: fields }).map((_, index) => (
      <Box key={index}>
        <Skeleton variant="text" width={100} height={20} sx={{ mb: 1 }} />
        <Skeleton variant="rounded" width="100%" height={56} />
      </Box>
    ))}
  </Box>
);

// Progress bar with label
export const ProgressBar = ({ value, label, color = 'primary' }) => (
  <Box sx={{ width: '100%' }}>
    {label && (
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="body2" fontWeight={600}>
          {Math.round(value)}%
        </Typography>
      </Box>
    )}
    <LinearProgress
      variant="determinate"
      value={value}
      color={color}
      sx={{
        height: 8,
        borderRadius: 4,
        backgroundColor: (theme) => alpha(theme.palette[color].main, 0.12),
        '& .MuiLinearProgress-bar': {
          borderRadius: 4,
        },
      }}
    />
  </Box>
);

// Inline loading
export const InlineLoader = ({ size = 20 }) => (
  <CircularProgress size={size} thickness={4} />
);

// Button loading state wrapper
export const ButtonLoader = ({ loading, children, ...props }) => (
  <Box sx={{ position: 'relative', display: 'inline-flex' }} {...props}>
    {children}
    {loading && (
      <CircularProgress
        size={24}
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          marginTop: '-12px',
          marginLeft: '-12px',
        }}
      />
    )}
  </Box>
);

const LoadingState = PageLoader;

export default LoadingState;
